import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { MenuItem } from 'primeng/api';

@Component({
  selector: 'z-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  display$ = new BehaviorSubject<boolean>(true);
  items$ = new BehaviorSubject<MenuItem[]>([]);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translocoService: TranslocoService,
    private titleService: Title
  ) {}

  ngOnInit() {
    this.translocoService
      .selectTranslation()
      .pipe(take(1))
      .subscribe(() => {
        this.generate();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.generate();
      });

    this.translocoService.events$
      .pipe(
        filter((event) => event.type === 'langChanged'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onLanguageChange();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  onLanguageChange() {
    this.translocoService
      .selectTranslation()
      .pipe(take(1))
      .subscribe(() => {
        this.generate();
      });
  }

  generate() {
    let items: MenuItem[] = [];

    items.push({ icon: 'fad fa-home', routerLink: '/home' });

    items = [...items, ...this.generateChildren(this.activatedRoute.root)];

    // hide breadcrumbs bar when there are no children
    if (items.length <= 1) {
      this.display$.next(false);
    } else {
      this.display$.next(true);
    }

    this.items$.next(items);

    const lastEntry = [...items].pop();

    if (!lastEntry || lastEntry.routerLink === '/home') {
      this.titleService.setTitle(`${this.translate('core.layout.title')}`);
    } else {
      this.titleService.setTitle(`${this.translate('core.layout.title')} - ${lastEntry.label}`);
    }
  }

  generateChildren(route: ActivatedRoute, url: string = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');

      if (routeURL !== '') {
        url += `/${routeURL}`;
      } else {
        return breadcrumbs ?? [];
      }

      const routerLink = [...url.split('/')];

      if (routeURL !== 'home' && child.snapshot.data?.breadcrumb) {
        const data = child.snapshot.data?.breadcrumb;

        if (data.hasParams && child.snapshot.paramMap.keys.length > 0) {
          const params: Record<string, string> = {};

          for (const key of child.snapshot.paramMap.keys) {
            params[key] = child.snapshot.params[key];
          }

          breadcrumbs.push({ label: this.translate(data.label, params), routerLink });
        } else {
          breadcrumbs.push({ label: this.translate(data.label), routerLink });
        }
      }

      return this.generateChildren(child, url, breadcrumbs);
    }

    return [];
  }
}
